"use client";

import { Line } from "react-chartjs-2";

import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from "chart.js";
import { usePortfolioEstimate } from "../hooks/query/queries";
import { Skeleton } from "./ui/skeleton";
import { Star } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";
import dayjs from "dayjs";
import { useAuth } from "../context/AuthContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler
);

export function PerformanceChart({ userId, allStartDate, loading }) {
  const { settings } = useAuth();
  const timeFilters = ["All", "1D", "1W", "1M", "6M", "1Y"];

  const [timeFilter, setTimeFilter] = useState("1M");
  const [finalResponse, setFinalResponse] = useState(null);

  const getDateRange = React.useCallback(
    (filter) => {
      const endDate = dayjs();
      let startDate = endDate;

      switch (filter) {
        case "1D":
          startDate = endDate;
          break;
        case "1W":
          startDate = endDate.subtract(6, "day");
          break;
        case "1M":
          startDate = endDate.subtract(1, "month");
          break;
        case "6M":
          startDate = endDate.subtract(6, "month");
          break;
        case "1Y":
          startDate = endDate.subtract(1, "year");
          break;
        default:
          startDate = dayjs(allStartDate);
      }

      return {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      };
    },
    [allStartDate]
  );

  const dateRange = useMemo(() => {
    const range = getDateRange(timeFilter);
    return {
      startDate: range.startDate.split("T")[0],
      endDate: range.endDate.split("T")[0],
    };
  }, [timeFilter, getDateRange]);

  const { data: portfolioEstimateData, isLoading: loadingGraphData } =
    usePortfolioEstimate(userId, dateRange.startDate, dateRange.endDate);

  const generateTimeline = React.useCallback(
    (filter, rawData = []) => {
      const now = dayjs();
      let startDate;
      let maxMonths = 12;

      switch (filter) {
        case "6M":
          startDate = now.subtract(5, "month").startOf("month");
          break;
        case "1Y":
          startDate = now.subtract(11, "month").startOf("month");
          break;
        case "All":
          startDate = dayjs(allStartDate).startOf("month");
          const monthsDiff = now.diff(startDate, "month");
          maxMonths = Math.max(monthsDiff + 1, 12);
          break;
        default:
          return [];
      }

      const timeline = [];
      for (let i = 0; i < maxMonths; i++) {
        const point = startDate.add(i, "month");

        if (point.isAfter(now)) break;
        timeline.push(point.valueOf());
      }

      const firstDataPoint = rawData.length > 0 ? rawData[0].x : null;
      if (firstDataPoint && !timeline.includes(firstDataPoint)) {
        timeline.push(firstDataPoint);
      }

      const lastDataPoint =
        rawData.length > 0 ? rawData[rawData.length - 1].x : null;
      if (lastDataPoint && !timeline.includes(lastDataPoint)) {
        timeline.push(lastDataPoint);
      }

      return timeline.sort((a, b) => a - b);
    },
    [allStartDate]
  );

  const normalizeGraphData = React.useCallback((timeline, rawData) => {
    const dataArray = Array.isArray(rawData) ? rawData : [];
    const sortedData = [...dataArray].sort((a, b) => {
      if (a.x === b.x) {
        return b.y - a.y;
      }
      return a.x - b.x;
    });

    if (sortedData.length === 0) {
      return timeline.map((timestamp) => ({ x: timestamp, y: 0 }));
    }

    return timeline.map((timestamp, index) => {
      if (index === 0) {
        const dataBefore = sortedData.filter((p) => p.x <= timestamp);
        if (dataBefore.length > 0) {
          return {
            x: timestamp,
            y: dataBefore[dataBefore.length - 1].y,
          };
        }
        return { x: timestamp, y: 0 };
      }

      if (index === timeline.length - 1) {
        const dataUpTo = sortedData.filter((p) => p.x <= timestamp);
        if (dataUpTo.length > 0) {
          return {
            x: timestamp,
            y: dataUpTo[dataUpTo.length - 1].y,
          };
        }
        return { x: timestamp, y: 0 };
      }

      const matches = sortedData.filter((point) => {
        return dayjs(point.x).isSame(timestamp, "month");
      });

      const nonZeroMatches = matches.filter((m) => m.y > 0);
      const match =
        nonZeroMatches.length > 0
          ? nonZeroMatches[nonZeroMatches.length - 1]
          : matches.length > 0
            ? matches[matches.length - 1]
            : null;

      if (!match) {
        const dataBefore = sortedData.filter((p) => p.x < timestamp);
        if (dataBefore.length > 0) {
          return {
            x: timestamp,
            y: dataBefore[dataBefore.length - 1].y,
          };
        }
      }

      return {
        x: timestamp,
        y: match?.y ?? 0,
      };
    });
  }, []);

  useEffect(() => {
    if (!portfolioEstimateData) {
      setFinalResponse(null);
      return;
    }

    const finalRes = portfolioEstimateData || { graphData: [] };

    if (!Array.isArray(finalRes.graphData)) {
      finalRes.graphData = [];
    }

    if (finalRes.graphData && finalRes.graphData.length > 0) {
      const dataMap = new Map();
      finalRes.graphData.forEach((point) => {
        const existing = dataMap.get(point.x);
        if (!existing) {
          dataMap.set(point.x, point);
        } else {
          if (point.y > existing.y) {
            dataMap.set(point.x, point);
          }
        }
      });

      finalRes.graphData = Array.from(dataMap.values()).sort(
        (a, b) => a.x - b.x
      );
    }

    let timeline = [];
    if (["6M", "1Y", "All"].includes(timeFilter)) {
      timeline = generateTimeline(timeFilter, finalRes.graphData || []);
      finalRes.graphData = normalizeGraphData(
        timeline,
        finalRes.graphData || []
      );
    }

    setFinalResponse(finalRes);
  }, [portfolioEstimateData, timeFilter, generateTimeline, normalizeGraphData]);

  const sortedGraphData = useMemo(() => {
    return finalResponse?.graphData
      ? [...finalResponse.graphData].sort((a, b) => a.x - b.x)
      : [];
  }, [finalResponse?.graphData]);

  const isProfit = useMemo(
    () => finalResponse?.profitOrLoss === "Profit",
    [finalResponse?.profitOrLoss]
  );
  const isLoss = useMemo(
    () => finalResponse?.profitOrLoss === "Loss",
    [finalResponse?.profitOrLoss]
  );

  const currentValue = useMemo(() => {
    return sortedGraphData.length > 0
      ? sortedGraphData[sortedGraphData.length - 1].y
      : finalResponse?.currentValue || 0;
  }, [sortedGraphData, finalResponse?.currentValue]);

  const getLabels = useCallback(() => {
    if (timeFilter === "1W" && sortedGraphData.length > 0) {
      return sortedGraphData.map((point) => {
        const date = dayjs(point.x);
        const dayName = date.format("ddd");
        return dayName === "Thu" ? "Thur" : dayName;
      });
    }
    return sortedGraphData.map((point) => dayjs(point.x).format("MMM D"));
  }, [timeFilter, sortedGraphData]);

  const getHighlightedPoint = useCallback(() => {
    if (sortedGraphData.length === 0) return null;

    if (timeFilter === "1W") {
      const midIndex = Math.floor(sortedGraphData.length / 2);
      if (midIndex >= 0 && midIndex < sortedGraphData.length) {
        const point = sortedGraphData.find((_, idx) => idx === midIndex);
        if (point) {
          return {
            index: midIndex,
            point,
          };
        }
      }
    }

    if (isProfit) {
      const maxPoint = sortedGraphData.reduce(
        (max, point, idx) => (point.y > max.y ? { point, index: idx } : max),
        { point: sortedGraphData[0], index: 0 }
      );
      return maxPoint;
    } else if (isLoss) {
      const minPoint = sortedGraphData.reduce(
        (min, point, idx) => (point.y < min.y ? { point, index: idx } : min),
        { point: sortedGraphData[0], index: 0 }
      );
      return minPoint;
    }

    return null;
  }, [sortedGraphData, timeFilter, isProfit, isLoss]);

  const highlightedPoint = useMemo(
    () => getHighlightedPoint(),
    [getHighlightedPoint]
  );

  const getHighlightedPointChange = useCallback(() => {
    if (!highlightedPoint || sortedGraphData.length === 0) return null;
    const firstValue = sortedGraphData[0].y;
    const highlightedValue = highlightedPoint.point.y;
    return highlightedValue - firstValue;
  }, [highlightedPoint, sortedGraphData]);

  const highlightedChange = useMemo(
    () => getHighlightedPointChange(),
    [getHighlightedPointChange]
  );

  const lineColor = useMemo(() => (isLoss ? "#EF4444" : "#14B8A6"), [isLoss]);
  const fillColor = useMemo(
    () => (isLoss ? "rgba(239, 68, 68, 0.1)" : "rgba(20, 184, 166, 0.1)"),
    [isLoss]
  );

  const verticalLinePlugin = useMemo(
    () => ({
      id: "verticalLine",
      afterDraw: (chart) => {
        if (!highlightedPoint || highlightedChange === null) return;

        const ctx = chart.ctx;
        const xScale = chart.scales.x;
        const yScale = chart.scales.y;
        const xPos = xScale.getPixelForValue(highlightedPoint.index);
        const yPos = yScale.getPixelForValue(highlightedPoint.point.y);

        ctx.save();
        ctx.strokeStyle = "#EF4444";
        ctx.lineWidth = 1;
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(xPos, yScale.top);
        ctx.lineTo(xPos, yScale.bottom);
        ctx.stroke();
        ctx.restore();

        ctx.save();
        ctx.fillStyle = "#EF4444";
        ctx.beginPath();
        ctx.arc(xPos, yPos, 4, 0, 2 * Math.PI);
        ctx.fill();
        ctx.restore();

        const labelText = `${dayjs(highlightedPoint.point.x).format("D MMM")} ${highlightedChange >= 0 ? "+" : ""}R${Math.abs(highlightedChange).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }).replace(/,/g, " ")}`;
        ctx.save();
        ctx.font = "12px sans-serif";
        ctx.fillStyle = "#1F2937";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(labelText, xPos, yScale.top - 10);
        ctx.restore();
      },
    }),
    [highlightedPoint, highlightedChange]
  );

  const chartData = useMemo(
    () => ({
      labels: getLabels(),
      datasets: [
        {
          label: "Portfolio Value (R)",
          data: sortedGraphData.map((point) => point.y),
          fill: true,
          borderColor: lineColor,
          backgroundColor: fillColor,
          tension: 0.4,
          pointRadius: sortedGraphData.map((_, index) =>
            highlightedPoint && index === highlightedPoint.index ? 5 : 0
          ),
          pointBackgroundColor: sortedGraphData.map((_, index) =>
            highlightedPoint && index === highlightedPoint.index
              ? "#EF4444"
              : "transparent"
          ),
          pointHoverRadius: 4,
          borderWidth: 2,
        },
      ],
    }),
    [getLabels, sortedGraphData, lineColor, fillColor, highlightedPoint]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          titleColor: "#1F2937",
          bodyColor: "#1F2937",
          borderColor: "#E5E7EB",
          borderWidth: 1,
          padding: 12,
          titleFont: {
            size: 12,
            weight: "normal",
          },
          bodyFont: {
            size: 12,
            weight: "normal",
          },
          displayColors: false,
          callbacks: {
            title: (context) => {
              const index = context[0]?.dataIndex;
              if (
                typeof index === "number" &&
                index >= 0 &&
                index < sortedGraphData.length
              ) {
                const point = sortedGraphData.find((_, idx) => idx === index);
                if (point) {
                  return dayjs(point.x).format("D MMM, YYYY");
                }
              }
              return "";
            },
            label: (context) => {
              if (settings?.digitalWallet?.showBalances === false) {
                return "****";
              }
              const value = context.parsed.y;
              return `R ${value
                .toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
                .replace(/,/g, " ")}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: "#9D96AF",
            font: {
              size: 12,
              weight: "normal",
            },
          },
        },
        y: {
          beginAtZero: false,
          grid: {
            display: true,
            color: "rgba(0, 0, 0, 0.05)",
          },
          ticks: {
            color: "#9D96AF",
            callback: (value) => {
              const numValue = Number(value);
              if (numValue >= 1000) {
                const kValue = numValue / 1000;
                if (kValue % 1 === 0) {
                  return `${kValue.toFixed(0)}k`;
                }
                return `${kValue.toFixed(1)}k`;
              }
              if (numValue < 1 && numValue > 0) {
                return numValue.toFixed(1);
              }
              return numValue
                .toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })
                .replace(/,/g, " ");
            },
            font: {
              size: 12,
              weight: "normal",
            },
          },
        },
      },
    }),
    [settings?.digitalWallet?.showBalances, sortedGraphData]
  );

  const formatDateRange = useCallback((startDate, endDate) => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    if (start.isSame(end, "day")) {
      return end.format("D MMM, YYYY");
    }

    if (start.isSame(end, "month") && start.isSame(end, "year")) {
      return `${start.format("D")} - ${end.format("D MMM, YYYY")}`;
    }

    if (start.isSame(end, "year")) {
      return `${start.format("D MMM")} - ${end.format("D MMM, YYYY")}`;
    }

    return `${start.format("D MMM, YYYY")} - ${end.format("D MMM, YYYY")}`;
  }, []);

  const formatCurrency = useCallback((value) => {
    return `R${Number(value)
      .toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      .replace(/,/g, " ")}`;
  }, []);

  const changeData = useMemo(() => {
    if (!finalResponse || sortedGraphData.length === 0) {
      return { amount: 0, percentage: 0, isProfit: false };
    }

    const startValue = sortedGraphData[0].y;
    const endValue = sortedGraphData[sortedGraphData.length - 1].y;
    const changeAmount = endValue - startValue;
    const changePercentage =
      startValue !== 0 ? ((changeAmount / startValue) * 100).toFixed(0) : 0;

    return {
      amount: Math.abs(changeAmount),
      percentage: Math.abs(changePercentage),
      isProfit: changeAmount >= 0,
    };
  }, [finalResponse, sortedGraphData]);

  const displayDateRange = useMemo(() => {
    if (finalResponse?.startDate && finalResponse?.endDate) {
      return formatDateRange(finalResponse.startDate, finalResponse.endDate);
    }
    const { startDate, endDate } = getDateRange(timeFilter);
    return formatDateRange(startDate, endDate);
  }, [
    finalResponse?.startDate,
    finalResponse?.endDate,
    timeFilter,
    getDateRange,
    formatDateRange,
  ]);
  return (
    <>
      {loading || loadingGraphData ? (
        <div className="p-6 bg-white rounded-xl max-w-4xl mx-auto space-y-6">
          <div className="flex justify-between items-center">
            <Skeleton className="h-5 w-40 bg-gray-300" />
            <div className="flex gap-2">
              {[...Array(6)].map((_, i) => (
                <Skeleton
                  key={i}
                  className="h-8 w-10 rounded-full bg-gray-300"
                />
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Skeleton className="h-6 w-32 bg-gray-300" />
            <div className="flex gap-2 items-center">
              <Skeleton className="h-4 w-16 bg-gray-300" />
              <Skeleton className="h-4 w-48 bg-gray-300" />
            </div>
          </div>

          <Skeleton className="h-72 w-full rounded-lg bg-gray-300" />

          <div className="flex justify-between items-center text-xs text-gray-600">
            <Skeleton className="h-4 w-3/5 bg-gray-300" />
            <div className="flex items-center gap-2">
              <Star className="text-purple-300" size={16} />
              <Skeleton className="h-4 w-32 bg-gray-300" />
            </div>
          </div>
        </div>
      ) : (
        <div className="md:p-6 md:bg-white bg-transparent md:rounded-xl mx-auto h-full flex flex-col">
          <div className="flex md:justify-between md:flex-row flex-col md:items-center mb-6 gap-4">
            <div>
              <h1 className="text-[20px] font-semibold text-[#6D6584]">
                Performance Overview
              </h1>
            </div>
            <ToggleGroup
              type="single"
              value={timeFilter}
              onValueChange={(value) => value && setTimeFilter(value)}
              className="md:border md:border-[1px] md:border-[#EBE9EF] md:rounded-full h-[48px] p-1 gap-5 md:gap-1"
            >
              {timeFilters.map((filter) => (
                <ToggleGroupItem
                  value={filter}
                  key={filter}
                  className={`rounded-full px-4 py-1 text-sm font-medium transition-all min-w-[50px] ${filter === "All" || filter === "1D" || filter === "1W" ? "w-[30%]" : "hidden md:block"} ${
                    timeFilter === filter
                      ? "bg-[#33125F] hover:bg-[#33125F] hover:text-white text-white shadow-sm"
                      : "bg-transparent text-gray-600 border border-[1px] border-[#EBE9EF] md:border-none"
                  }`}
                >
                  <span className="md:hidden">
                    {filter === "All" ? "All time" : filter}
                  </span>
                  <span className="hidden md:inline">{filter}</span>
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>

          <div className="mb-6">
            <h2 className="text-[24px] font-bold text-[#33125F] mb-2">
              {settings?.digitalWallet?.showBalances === false
                ? "****"
                : formatCurrency(currentValue)}
              *
            </h2>
            <p
              className={`text-sm flex gap-1 items-center flex-wrap font-semibold ${changeData.isProfit ? "text-[#17C3A5]" : "text-[#DA2F58]"}`}
            >
              <span>
                {changeData.isProfit ? "+" : "-"} R
                {settings?.digitalWallet?.showBalances === false
                  ? "****"
                  : changeData.amount
                      .toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                      .replace(/,/g, " ")}
              </span>
              <span>
                (
                {settings?.digitalWallet?.showBalances === false
                  ? "**"
                  : changeData.percentage}
                %)
              </span>
              <span className="text-[#6D6584] font-medium">
                between {displayDateRange}
              </span>
            </p>
          </div>

          <div className="relative mb-4 flex-1 min-h-[300px]">
            <Line
              data={chartData}
              options={chartOptions}
              plugins={[verticalLinePlugin]}
            />
          </div>

          <div className="flex md:flex-row flex-col md:gap-0 gap-5 justify-between items-center text-xs text-gray-600 mt-4 pt-4 border-t border-gray-100 md:mb-0 mb-5">
            <p className="flex-1 text-[#6D6584] text-[12px]">
              *Includes additions to the starting balance of your portfolio
              during the specified time frame.
            </p>
            <div className="flex items-center gap-2 md:ml-4 ml-0 bg-[#EBE9EF4D] md:bg-transparent md:w-auto w-full md:justify-end justify-center md:h-auto h-[48px] rounded-full">
              <Star
                className={
                  changeData.isProfit
                    ? "text-[#17C3A5] fill-[#17C3A5]"
                    : "text-[#DA2F58] fill-[#DA2F58]"
                }
                size={14}
              />
              <span className="text-[#6D6584] font-normal text-[14px] ml-1">
                All time performance:{" "}
                <span
                  className={
                    changeData.isProfit
                      ? "text-[#17C3A5] ml-1 font-semibold"
                      : "text-[#DA2F58] ml-1 font-semibold"
                  }
                >
                  {changeData.isProfit ? "↑" : "↓"}R
                  {settings?.digitalWallet?.showBalances === false
                    ? "****"
                    : finalResponse?.allTimePerformance
                      ? finalResponse.allTimePerformance
                          .toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                          .replace(/,/g, " ")
                      : changeData.amount
                          .toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                          .replace(/,/g, " ")}
                </span>
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
